import axios from 'axios';

export const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://zynq-crm-api.onrender.com';
const API = `${API_URL}/api`;

const api = axios.create({
  baseURL: API,
  withCredentials: false,
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 responses — only clear tokens, let React routing handle redirect
// DO NOT use window.location.href here — it kills the React tree mid-render
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Skip for auth endpoints — AuthContext handles these
      const url = error.config?.url || '';
      if (!url.includes('/auth/me') && !url.includes('/auth/login') && !url.includes('/auth/register')) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  googleSession: (session_id) => api.post('/auth/google/session', { session_id }),
  me: () => api.get('/auth/me'),
  myPermissions: () => api.get('/auth/my-permissions'),
  logout: () => api.post('/auth/logout'),
  users: () => api.get('/auth/users'),
  adminCreateUser: (data) => api.post('/auth/admin/users', data),
};

// Leads
export const leadsAPI = {
  list: (params) => api.get('/leads', { params }),
  get: (id) => api.get(`/leads/${id}`),
  create: (data) => api.post('/leads', data),
  update: (id, data) => api.put(`/leads/${id}`, data),
  delete: (id) => api.delete(`/leads/${id}`),
  updateStage: (id, stage) => api.patch(`/leads/${id}/stage`, { stage }),
  assign: (id, assigned_to) => api.patch(`/leads/${id}/assign`, { assigned_to }),
  bulkAssign: (lead_ids, assigned_to) => api.post('/leads/bulk-assign', { lead_ids, assigned_to }),
  importCSV: (formData) => api.post('/leads/import-csv', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
};

// Properties
export const propertiesAPI = {
  list: (params) => api.get('/properties', { params }),
  get: (id) => api.get(`/properties/${id}`),
  create: (data) => api.post('/properties', data),
  update: (id, data) => api.put(`/properties/${id}`, data),
  delete: (id) => api.delete(`/properties/${id}`),
  uploadPhoto: (formData) => api.post('/properties/upload-photo', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  getLeads: (id) => api.get(`/properties/${id}/leads`),
  linkLead: (data) => api.post('/properties/link-lead', data),
  unlinkLead: (linkId) => api.delete(`/properties/unlink-lead/${linkId}`),
};

// Activities
export const activitiesAPI = {
  list: (params) => api.get('/activities', { params }),
  create: (data) => api.post('/activities', data),
  delete: (id) => api.delete(`/activities/${id}`),
};

// Follow-ups
export const followUpsAPI = {
  list: (params) => api.get('/follow-ups', { params }),
  create: (data) => api.post('/follow-ups', data),
  update: (id, data) => api.put(`/follow-ups/${id}`, data),
  delete: (id) => api.delete(`/follow-ups/${id}`),
};

// Dashboard
export const dashboardAPI = {
  get: (params) => api.get('/dashboard', { params }),
};

// Settings
export const settingsAPI = {
  get: () => api.get('/settings'),
  update: (data) => api.put('/settings', data),
};

// Capture (public)
export const captureAPI = {
  getConfig: () => axios.get(`${API}/capture/form-config`),
  submit: (data) => axios.post(`${API}/capture/submit`, data),
};

// Workshop Builder (public + admin)
export const workshopBuilderAPI = {
  // Public
  listPublic: () => axios.get(`${API}/workshop-builder/templates`),
  getPublic: (id) => axios.get(`${API}/workshop-builder/templates/${id}`),
  submit: (data) => axios.post(`${API}/workshop-builder/submit`, data),
  // Admin
  listAdmin: () => api.get('/workshop-builder/admin/templates'),
  getAdmin: (id) => api.get(`/workshop-builder/admin/templates/${id}`),
  create: (data) => api.post('/workshop-builder/admin/templates', data),
  update: (id, data) => api.put(`/workshop-builder/admin/templates/${id}`, data),
  remove: (id) => api.delete(`/workshop-builder/admin/templates/${id}`),
};

// Generic builder API factory (used by scrum / beweegaanbod / pakket)
const makeBuilderAPI = (prefix) => ({
  listPublic: () => axios.get(`${API}/${prefix}/templates`),
  getPublic: (id) => axios.get(`${API}/${prefix}/templates/${id}`),
  submit: (data) => axios.post(`${API}/${prefix}/submit`, data),
  listAdmin: () => api.get(`/${prefix}/admin/templates`),
  getAdmin: (id) => api.get(`/${prefix}/admin/templates/${id}`),
  create: (data) => api.post(`/${prefix}/admin/templates`, data),
  update: (id, data) => api.put(`/${prefix}/admin/templates/${id}`, data),
  remove: (id) => api.delete(`/${prefix}/admin/templates/${id}`),
});

export const scrumBuilderAPI = makeBuilderAPI('scrum-builder');
export const beweegaanbodBuilderAPI = makeBuilderAPI('beweegaanbod-builder');
export const pakketBuilderAPI = makeBuilderAPI('pakket-builder');

// Meeting Timer (Tool Hub) — public + admin
export const meetingTimerAPI = {
  // Public
  getConfig: () => axios.get(`${API}/meeting-timer/config`),
  start: (data) => axios.post(`${API}/meeting-timer/start`, data),
  // Admin
  adminGetConfig: () => api.get('/meeting-timer/admin/config'),
  adminUpdateConfig: (data) => api.put('/meeting-timer/admin/config', data),
  adminResetDefaults: () => api.post('/meeting-timer/admin/reset-defaults'),
  adminListSessions: (limit = 100) => api.get('/meeting-timer/admin/sessions', { params: { limit } }),
};

// Ebook Library (Tool Hub) — public + admin
export const ebookLibraryAPI = {
  // Public
  getConfig: () => axios.get(`${API}/ebook-library/config`),
  listPublic: () => axios.get(`${API}/ebook-library/library`),
  requestAccess: (data) => axios.post(`${API}/ebook-library/access`, data),
  viewUrl: (id, token) => `${API}/ebook-library/view/${id}?token=${encodeURIComponent(token)}`,
  downloadUrl: (id, token) => `${API}/ebook-library/download/${id}?token=${encodeURIComponent(token)}`,
  // Admin
  adminGetConfig: () => api.get('/ebook-library/admin/config'),
  adminUpdateConfig: (data) => api.put('/ebook-library/admin/config', data),
  adminList: () => api.get('/ebook-library/admin/ebooks'),
  adminGet: (id) => api.get(`/ebook-library/admin/ebooks/${id}`),
  adminCreate: (data) => api.post('/ebook-library/admin/ebooks', data),
  adminUpdate: (id, data) => api.put(`/ebook-library/admin/ebooks/${id}`, data),
  adminDelete: (id) => api.delete(`/ebook-library/admin/ebooks/${id}`),
  adminUploadPdf: (formData) => api.post('/ebook-library/admin/upload-pdf', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  // Chunked PDF upload (voor grote bestanden)
  adminUploadPdfInit: (data) => api.post('/ebook-library/admin/upload-pdf/init', data),
  adminUploadPdfChunk: (formData) => api.post('/ebook-library/admin/upload-pdf/chunk', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  adminUploadPdfFinalize: (data) => api.post('/ebook-library/admin/upload-pdf/finalize', data),
  adminUploadCover: (formData) => api.post('/ebook-library/admin/upload-cover', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  adminListGrants: (limit = 100) => api.get('/ebook-library/admin/grants', { params: { limit } }),
};

// Team
export const teamAPI = {
  list: () => api.get('/team'),
  members: () => api.get('/team/members'),
  updateRole: (userId, role) => api.put(`/team/${userId}/role`, { role }),
};

// Reports
export const reportsAPI = {
  leadConversion: (params) => api.get('/reports/lead-conversion', { params }),
  agentPerformance: (params) => api.get('/reports/agent-performance', { params }),
  revenue: (params) => api.get('/reports/revenue', { params }),
  leadSourceROI: (params) => api.get('/reports/lead-source-roi', { params }),
  exportExcel: (type, params) => `${API}/reports/export/excel/${type}?time_range=${params?.time_range || '30d'}`,
  exportPDF: (type, params) => `${API}/reports/export/pdf/${type}?time_range=${params?.time_range || '30d'}`,
};

// RBAC
export const rbacAPI = {
  getMyPermissions: () => api.get('/rbac/my-permissions'),
  getPermissions: () => api.get('/rbac/permissions'),
  getRoles: () => api.get('/rbac/roles'),
  createRole: (data) => api.post('/rbac/roles', data),
  updateRole: (id, data) => api.put(`/rbac/roles/${id}`, data),
  deleteRole: (id) => api.delete(`/rbac/roles/${id}`),
  assignRole: (userId, role) => api.put(`/rbac/users/${userId}/role`, { role }),
};

// Calendar
export const calendarAPI = {
  getEvents: (params) => api.get('/calendar', { params }),
  createEvent: (data) => api.post('/calendar/events', data),
  updateEvent: (id, data, params) => api.put(`/calendar/events/${id}`, data, { params }),
  deleteEvent: (id, params) => api.delete(`/calendar/events/${id}`, { params }),
  getEvent: (id) => api.get(`/calendar/events/${id}`),
};

// Content Planner (Trello-style board)
export const contentPlannerAPI = {
  getBoard: () => api.get('/content-planner/board'),
  // Lists
  createList: (data) => api.post('/content-planner/lists', data),
  updateList: (id, data) => api.put(`/content-planner/lists/${id}`, data),
  deleteList: (id) => api.delete(`/content-planner/lists/${id}`),
  reorderLists: (list_ids) => api.post('/content-planner/lists/reorder', { list_ids }),
  // Cards
  createCard: (data) => api.post('/content-planner/cards', data),
  getCard: (id) => api.get(`/content-planner/cards/${id}`),
  updateCard: (id, data) => api.put(`/content-planner/cards/${id}`, data),
  deleteCard: (id) => api.delete(`/content-planner/cards/${id}`),
  moveCard: (id, list_id, position) => api.post(`/content-planner/cards/${id}/move`, { list_id, position }),
  // Checklist
  addChecklistItem: (cardId, text) => api.post(`/content-planner/cards/${cardId}/checklist`, { text }),
  updateChecklistItem: (cardId, itemId, data) => api.put(`/content-planner/cards/${cardId}/checklist/${itemId}`, data),
  deleteChecklistItem: (cardId, itemId) => api.delete(`/content-planner/cards/${cardId}/checklist/${itemId}`),
};

// Integrations
export const integrationsAPI = {
  getMessages: (params) => api.get('/integrations/messages', { params }),
  // Email
  getEmailConfig: () => api.get('/integrations/email/config'),
  updateEmailConfig: (data) => api.put('/integrations/email/config', data),
  testEmailConnection: () => api.post('/integrations/email/test-connection'),
  sendEmail: (data) => api.post('/integrations/email/send', data),
  // Resend → Contacts sync
  listResendAudiences: () => api.get('/integrations/email/resend/audiences'),
  resendSyncAll: () => api.post('/integrations/email/resend/sync-all'),
  resendSyncStats: () => api.get('/integrations/email/resend/sync-stats'),
};

// Automations
export const automationsAPI = {
  getRules: () => api.get('/automations/rules'),
  createRule: (data) => api.post('/automations/rules', data),
  updateRule: (id, data) => api.put(`/automations/rules/${id}`, data),
  deleteRule: (id) => api.delete(`/automations/rules/${id}`),
  updateGlobalLimits: (data) => api.put('/automations/global-limits', data),
  getLog: (params) => api.get('/automations/log', { params }),
  process: () => api.post('/automations/process'),
};

// Vitaliteit Benchmark 2026 (publieke tool + admin beheer)
export const vitaliteitBenchmarkAPI = {
  // Public
  getConfig: () => api.get('/vitaliteit-benchmark/config'),
  submit: (data) => api.post('/vitaliteit-benchmark/submit', data),
  // Admin
  adminGetConfig: () => api.get('/vitaliteit-benchmark/admin/config'),
  adminUpdateConfig: (data) => api.put('/vitaliteit-benchmark/admin/config', data),
  adminResetConfig: () => api.post('/vitaliteit-benchmark/admin/config/reset'),
  adminListSubmissions: (limit = 50) => api.get('/vitaliteit-benchmark/admin/submissions', { params: { limit } }),
};

// Arbeidsvoorwaarden Check 2026 (publieke tool + admin beheer)
export const arbeidsvoorwaardenCheckAPI = {
  // Public
  getConfig: () => api.get('/arbeidsvoorwaarden-check/config'),
  submit: (data) => api.post('/arbeidsvoorwaarden-check/submit', data),
  // Admin
  adminGetConfig: () => api.get('/arbeidsvoorwaarden-check/admin/config'),
  adminUpdateConfig: (data) => api.put('/arbeidsvoorwaarden-check/admin/config', data),
  adminResetConfig: () => api.post('/arbeidsvoorwaarden-check/admin/config/reset'),
  adminResetChecklist: () => api.post('/arbeidsvoorwaarden-check/admin/checklist/reset'),
  adminListSubmissions: (limit = 50) => api.get('/arbeidsvoorwaarden-check/admin/submissions', { params: { limit } }),
};

// Pomodoro Timer (Tool Hub) — public + admin
export const pomodoroTimerAPI = {
  // Public
  getConfig: () => axios.get(`${API}/pomodoro-timer/config`),
  start: (data) => axios.post(`${API}/pomodoro-timer/start`, data),
  complete: (sessionId, data) => axios.post(`${API}/pomodoro-timer/${sessionId}/complete`, data),
  // Admin
  adminGetConfig: () => api.get('/pomodoro-timer/admin/config'),
  adminUpdateConfig: (data) => api.put('/pomodoro-timer/admin/config', data),
  adminResetDefaults: () => api.post('/pomodoro-timer/admin/reset-defaults'),
  adminListSessions: (limit = 100) => api.get('/pomodoro-timer/admin/sessions', { params: { limit } }),
};

// Forms Builder — publieke lead-capture formulieren + admin beheer
export const formsBuilderAPI = {
  // Public (no auth)
  getBySlug: (slug) => axios.get(`${API}/forms-builder/by-slug/${slug}`),
  submit: (slug, values) => axios.post(`${API}/forms-builder/submit/${slug}`, { values }),
  // Admin
  adminList: () => api.get('/forms-builder/admin/forms'),
  adminGet: (id) => api.get(`/forms-builder/admin/forms/${id}`),
  adminCreate: (data) => api.post('/forms-builder/admin/forms', data),
  adminUpdate: (id, data) => api.put(`/forms-builder/admin/forms/${id}`, data),
  adminDelete: (id) => api.delete(`/forms-builder/admin/forms/${id}`),
  adminSubmissions: (id, limit = 50) => api.get(`/forms-builder/admin/forms/${id}/submissions`, { params: { limit } }),
};

export default api;
